import React, { useState, useEffect } from "react";
import { TextField, Select } from "../ui";
import { journalsAPI } from "../services/api";
import { BankGlAccountDropdown } from "../shared/presentation/components";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";

interface VendorTransferToOtherAccountsRecoProps {
  transactionDate: string;
  bankAccountId?: string;
  onDateChange: (date: string) => void;
  onPaymentModeChange: (paymentMode: string) => void;
  onToAccountChange: (accountId: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  zohoGl?: string;
}

const VendorTransferToOtherAccountsReco: React.FC<
  VendorTransferToOtherAccountsRecoProps
> = ({
  transactionDate,
  bankAccountId,
  onDateChange,
  onPaymentModeChange,
  onToAccountChange,
  onReferenceNumberChange,
  zohoGl,
}) => {
  const [date, setDate] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<string>("Bank Transfer");
  const [toAccount, setToAccount] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [selectedAccountName, setSelectedAccountName] = useState<string>("");

  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  useEffect(() => {
    onPaymentModeChange(paymentMode);
  }, [paymentMode, onPaymentModeChange]);

  useEffect(() => {
    if (!zohoGl) {
      setToAccount("");
      setSelectedAccountName("");
      return;
    }

    setToAccount(zohoGl);
    onToAccountChange(zohoGl);

    journalsAPI
      .getGLMapByAccountId(zohoGl)
      .then((response) => {
        if (response.success && response.data) {
          const accountIdStr = String(response.data.account_id);
          setToAccount(accountIdStr);
          setSelectedAccountName(response.data.gl_name ?? "");
          onToAccountChange(accountIdStr);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch GL account by ID:", error);
      });
  }, [zohoGl, onToAccountChange]);

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handlePaymentModeChange = (mode: string) => {
    setPaymentMode(mode);
    onPaymentModeChange(mode);
  };

  const handleToAccountChange = (account: GLMappingEntity) => {
    const accountIdStr = String(account.account_id);
    setToAccount(accountIdStr);
    setSelectedAccountName(account.gl_name);
    onToAccountChange(accountIdStr);
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDateChange(event.target.value)
                }
              />
            </TextField>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Payment Mode <span className="text-error-600">*</span>
            </span>
            <Select
              label=""
              helpText=""
              value={paymentMode}
              onValueChange={handlePaymentModeChange}
            >
              <Select.Item value="Bank Remittance">Bank Remittance</Select.Item>
              <Select.Item value="Bank Transfer">Bank Transfer</Select.Item>
              <Select.Item value="Cash">Cash</Select.Item>
              <Select.Item value="Cheque">Cheque</Select.Item>
              <Select.Item value="Credit Card">Credit Card</Select.Item>
              <Select.Item value="UPI">UPI</Select.Item>
            </Select>
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              To Account <span className="text-error-600">*</span>
            </span>
            <BankGlAccountDropdown
              preset="transfer_to_other"
              bankAccountId={bankAccountId}
              selectedAccountId={toAccount}
              selectedAccountName={selectedAccountName}
              onSelect={handleToAccountChange}
              placeholder="Select account"
            />
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference #
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder="Enter reference number (optional)"
                value={referenceNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceNumberChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorTransferToOtherAccountsReco;
